import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import VerticalsPage from "@/pages/VerticalsPage";
import VerticalDetailPage from "@/pages/VerticalDetailPage";
import ProblemDetailPage from "@/pages/ProblemDetailPage";
import AISearchPage from "@/pages/AISearchPage";
import AISalesPage from "@/pages/AISalesPage";
import ProfilePage from "@/pages/ProfilePage";
import VideoTestimonialsPage from "@/pages/VideoTestimonialsPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import HappyCustomersPage from "@/pages/HappyCustomersPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AssessmentPage from "@/pages/AssessmentPage";
import CODNPage from "@/pages/CODNPage";
import SystemMapperPage from "@/pages/SystemMapperPage";
import ScheduleDemoPage from "@/pages/ScheduleDemoPage";
import ResourcesPage from "@/pages/ResourcesPage";

// Admin Pages (hidden route)
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminVerticals from "@/pages/admin/AdminVerticals";
import AdminProblems from "@/pages/admin/AdminProblems";
import AdminBranches from "@/pages/admin/AdminBranches";
import AdminExperts from "@/pages/admin/AdminExperts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLogos from "@/pages/admin/AdminLogos";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminKnowledgeBase from "@/pages/admin/AdminKnowledgeBase";
import AdminAISalesKnowledgeBase from "@/pages/admin/AdminAISalesKnowledgeBase";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminDatasets from "@/pages/admin/AdminDatasets";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminClients from "@/pages/admin/AdminClients"; // Client Master
import AdminCaseStudies from "@/pages/admin/AdminCaseStudies";
import AdminAssessments from "@/pages/admin/AdminAssessments";
import AdminCODN from "@/pages/admin/AdminCODN";
import AdminSystemMapper from "@/pages/admin/AdminSystemMapper";
import AdminResources from "@/pages/admin/AdminResources";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import PresentationGenerator from "@/pages/admin/PresentationGenerator";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminSEO from "@/pages/admin/AdminSEO";
import CollectionPlanningPage from "@/pages/CollectionPlanningPage";
import AdminProductMaster from "@/pages/admin/AdminProductMaster";
import PreSalesHub from "@/pages/PreSalesHub";
import ProductDemosPage from "@/pages/ProductDemosPage";
import FeedbackFormPublic from "@/pages/FeedbackFormPublic";

import AIConsultantSidebar from "@/components/AIConsultantSidebar";

// Auth guard component
const ProtectedRoute = ({ children, adminOnly = false, internalOnly = false, requiresCollections = false, requiresPresales = false }) => {
  const token = localStorage.getItem('auth_token');
  const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && userData.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (requiresCollections && userData.role !== 'admin' && !userData.can_access_collections) {
    return <Navigate to="/" replace />;
  }

  if (requiresPresales && userData.role !== 'admin' && !userData.can_access_presales) {
    return <Navigate to="/" replace />;
  }

  if (internalOnly && userData.user_type === 'external') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Root landing behavior:
//   - Not authenticated → /login
//   - Authenticated with Collections access (admin or can_access_collections=true) → /collection-planning
//   - Authenticated without Collections access → /landing (marketing home so they see something useful)
const RootRedirect = () => {
  const token = localStorage.getItem('auth_token');
  if (!token) return <Navigate to="/login" replace />;
  try {
    const u = JSON.parse(localStorage.getItem('user_data') || '{}');
    if (u.role === 'admin' || u.can_access_collections) {
      return <Navigate to="/collection-planning" replace />;
    }
  } catch { /* fall through */ }
  return <Navigate to="/landing" replace />;
};

function App() {
  return (
    <div className="min-h-screen bg-white">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Public Content Pages (no login required) */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/verticals" element={<VerticalsPage />} />
            <Route path="/vertical/:id" element={<VerticalDetailPage />} />
            <Route path="/problem/:id" element={<ProblemDetailPage />} />
            <Route path="/our-customers" element={<HappyCustomersPage />} />
            <Route path="/assessment" element={<AssessmentPage />} />
            <Route path="/codn-calculator" element={<CODNPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/schedule-demo" element={<ScheduleDemoPage />} />
            <Route path="/video-testimonials" element={<VideoTestimonialsPage />} />
            <Route path="/testimonials" element={<VideoTestimonialsPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />

            {/* Protected User Routes (login required) */}
          <Route path="/ai-search" element={
            <ProtectedRoute>
              <AISearchPage />
            </ProtectedRoute>
          } />
          <Route path="/ai-sales" element={
            <ProtectedRoute>
              <AISalesPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/system-mapper" element={
            <ProtectedRoute>
              <SystemMapperPage />
            </ProtectedRoute>
          } />

          {/* Public Feedback Form — no auth required */}
          <Route path="/feedback/:token" element={<FeedbackFormPublic />} />

          {/* Pre-Sales Hub — internal users */}
          <Route path="/pre-sales" element={
            <ProtectedRoute>
              <PreSalesHub />
            </ProtectedRoute>
          } />

          {/* Product Demos — internal users only */}
          <Route path="/product-demos" element={
            <ProtectedRoute internalOnly>
              <ProductDemosPage />
            </ProtectedRoute>
          } />
          
          {/* Hidden Admin Routes */}
          <Route path="/admin-portal" element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/verticals" element={
            <ProtectedRoute adminOnly>
              <AdminVerticals />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/problems" element={
            <ProtectedRoute adminOnly>
              <AdminProblems />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/branches" element={
            <ProtectedRoute adminOnly>
              <AdminBranches />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/experts" element={
            <ProtectedRoute adminOnly>
              <AdminExperts />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/settings" element={
            <ProtectedRoute adminOnly>
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/logos" element={
            <ProtectedRoute adminOnly>
              <AdminLogos />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/users" element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/knowledge-base" element={
            <ProtectedRoute adminOnly>
              <AdminKnowledgeBase />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/sales-knowledge-base" element={
            <ProtectedRoute adminOnly>
              <AdminAISalesKnowledgeBase />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/audit-log" element={
            <ProtectedRoute adminOnly>
              <AdminAuditLog />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/datasets" element={
            <ProtectedRoute adminOnly>
              <AdminDatasets />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/leads" element={
            <ProtectedRoute adminOnly>
              <AdminLeads />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/analytics" element={
            <ProtectedRoute adminOnly>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/clients" element={
            <ProtectedRoute adminOnly>
              <AdminClients />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/case-studies" element={
            <ProtectedRoute adminOnly>
              <AdminCaseStudies />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/assessments" element={
            <ProtectedRoute adminOnly>
              <AdminAssessments />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/codn" element={
            <ProtectedRoute adminOnly>
              <AdminCODN />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/system-mapper" element={
            <ProtectedRoute adminOnly>
              <AdminSystemMapper />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/resources" element={
            <ProtectedRoute adminOnly>
              <AdminResources />
            </ProtectedRoute>
          } />
          <Route path="/admin-portal/feedback" element={
            <ProtectedRoute adminOnly>
              <AdminFeedback />
            </ProtectedRoute>
          } />

          <Route path="/admin-portal/presentation-generator" element={
            <ProtectedRoute adminOnly>
              <PresentationGenerator />
            </ProtectedRoute>
          } />

          <Route path="/admin-portal/bookings" element={
            <ProtectedRoute adminOnly>
              <AdminBookings />
            </ProtectedRoute>
          } />

          <Route path="/admin-portal/seo" element={
            <ProtectedRoute adminOnly>
              <AdminSEO />
            </ProtectedRoute>
          } />

          <Route path="/collections" element={
            <ProtectedRoute requiresCollections>
              <CollectionPlanningPage />
            </ProtectedRoute>
          } />

          <Route path="/collection-planning" element={
            <ProtectedRoute requiresCollections>
              <CollectionPlanningPage />
            </ProtectedRoute>
          } />

          <Route path="/admin-portal/product-master" element={
            <ProtectedRoute adminOnly>
              <AdminProductMaster />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AIConsultantSidebar />
      </BrowserRouter>
        <Toaster position="bottom-right" richColors offset={16} />
      </div>
  );
}

export default App;
